import React from 'react';
import { motion } from 'framer-motion';
import { Button, GeometricMark, JaliPattern } from './ui/Shared';
import { GurmukhiWatermark, KesariDot } from './ui/Ornaments';
import { ArrowRight, Calendar, MapPin, Sparkles } from 'lucide-react';
import { useApply } from './ApplyContext';
export const Hero = () => {
  const {
    open
  } = useApply();
  return <section className="relative min-h-screen flex items-center justify-center bg-navy overflow-hidden pt-32" data-id="element-141">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-noise pointer-events-none z-10" data-id="element-142" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(20,40,73,1)_0%,rgba(11,28,54,1)_100%)]" data-id="element-143" />
      <JaliPattern className="absolute inset-0 text-gold opacity-20" data-id="element-144" />

      <GurmukhiWatermark text="ਖਾਲਸਾ" className="text-gold top-1/4 -left-20 opacity-[0.02] -rotate-12" data-id="element-145" />

      <div className="absolute -right-64 -top-64 w-[800px] h-[800px] text-gold/10 pointer-events-none" data-id="element-146">
        <GeometricMark data-id="element-147" />
      </div>

      <div className="relative z-20 max-w-7xl mx-auto px-6 w-full py-20" data-id="element-148">
        <div className="max-w-4xl" data-id="element-149">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="flex items-center gap-3 mb-8" data-id="element-150">
            <span className="text-ivory/70 text-xs sm:text-sm font-sans flex items-center gap-2 tracking-[0.2em] uppercase" data-id="element-151">
              <KesariDot className="w-1.5 h-1.5" data-id="element-152" />
              Civic Leadership Fellowship
              <span className="hidden sm:inline-block text-ivory/40" data-id="element-153">
                · 2026 Cohort
              </span>
            </span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="font-serif text-5xl md:text-7xl lg:text-8xl text-ivory leading-[1.1] tracking-tight mb-6 text-balance" data-id="element-154">
            Build courage.
            <br data-id="element-155" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold to-kesari-soft" data-id="element-156">
              Solve real problems.
            </span>
            <br data-id="element-157" />
            Lead with purpose.
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="font-sans text-xl md:text-2xl text-parchment/80 max-w-2xl mb-8" data-id="element-158">
            A 3-Saturday team-based fellowship for high school students in
            Brampton & the GTA.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="inline-block bg-indigo/50 border border-gold/20 px-4 py-2 rounded-sm mb-10 relative overflow-hidden group" data-id="element-159">
            <div className="absolute inset-0 bg-gradient-to-r from-kesari/0 via-kesari/10 to-kesari/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" data-id="element-160" />
            <p className="font-sans text-sm text-ivory relative z-10" data-id="element-161">
              <span className="text-gold font-medium" data-id="element-162">
                Not a lecture series.
              </span>{' '}
              A civic venture-building experience.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="flex flex-col sm:flex-row gap-4 mb-16 items-center" data-id="element-163">
            <Button onClick={open} variant="solid" className="w-full sm:w-auto" data-id="element-164">
              Apply by Aug 15, 2026
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-165" />
            </Button>
            <Button href="#program" variant="ghost" className="w-full sm:w-auto" data-id="element-166">
              See the program
            </Button>
            <motion.span initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 1
          }} className="font-gurmukhi text-kesari/60 text-sm ml-4 hidden md:block" data-id="element-167">
              ਚੜ੍ਹਦੀ ਕਲਾ
            </motion.span>
          </motion.div>

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 1,
          delay: 0.8
        }} className="grid grid-cols-1 md:grid-cols-3 gap-6 border-t border-gold/10 pt-8" data-id="element-168">
            <Pillar icon={<Sparkles size={18} data-id="element-170" />} title="Inaugural Cohort" detail="Fall 2026 — be part of the founding class." data-id="element-169" />
            <Pillar icon={<Calendar size={18} data-id="element-172" />} title="Three Saturdays" detail="An intensive, team-based format in Brampton." data-id="element-171" />
            <Pillar icon={<MapPin size={18} data-id="element-174" />} title="Mentor-Led" detail="Guided by founders, professors, and community leaders." data-id="element-173" />
          </motion.div>
        </div>
      </div>
    </section>;
};
const Pillar = ({
  icon,
  title,
  detail
}: {
  icon: React.ReactNode;
  title: string;
  detail: string;
}) => <motion.div whileHover={{
  y: -4
}} className="transition-transform duration-300" data-id="element-175">
    <div className="text-kesari mb-3" data-id="element-176">{icon}</div>
    <div className="font-serif text-lg text-ivory mb-1" data-id="element-177">{title}</div>
    <div className="font-sans text-xs text-parchment/60 leading-relaxed" data-id="element-178">
      {detail}
    </div>
  </motion.div>;